import { graphql, navigate, PageProps } from 'gatsby';
import React, { useEffect } from 'react';

import { Box, Container, Stack, Typography } from '@mui/material';
import { AnalyticsPageType } from '@shopify/hydrogen-react';
import slugify from '@sindresorhus/slugify';

import SeoHead from '../../../components/head';
import { HorizontalSlantContainer } from '../../../components/horizontal-slant-container';
import { ProductListing } from '../../../components/products/list';
import { Button } from '../../../components/ui';
import { useAnalytics } from '../../../shared/analytics';
import { headerSlantContainerNoContent } from '../../../shared/styles/headerSlantContainer';
import { antiSlugify } from '../../../shared/utils';

type ProductTypeIndexPageProps = PageProps<Queries.ProductTypeIndexQuery, { productType: string }>

export default function ProductTypeIndex({ data, pageContext, location }: ProductTypeIndexPageProps) {
  const { productType } = pageContext;
  const { products } = data;

  const unslugifiedProductType = antiSlugify(productType)
  const headerSlantContainerNoContentStyles = headerSlantContainerNoContent();

  const analytics = useAnalytics();
  useEffect(() => {
    if (products.nodes && products.nodes.length > 1) {
      analytics.trackPageView(AnalyticsPageType.collection, {
        collectionHandle: productType,
        products: products.nodes.map(e => {
          return {
            ...e,
            price: e.priceRangeV2.maxVariantPrice.amount
          }
        })
      });
    }
  }, [])

  useEffect(() => {
    if (products.nodes.length === 1) {
      navigate(`/products/${products.nodes[0].handle}` as any)
    }
  }, [products])

  if (products.nodes.length === 1) return <></>

  return (
    <>
      <HorizontalSlantContainer id={"product-type-list"} autoHeight>
        <Container sx={headerSlantContainerNoContentStyles}></Container>
      </HorizontalSlantContainer>

      <Container sx={{ pt: 6 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h1" gutterBottom>
              {unslugifiedProductType}
            </Typography>
          </Box>

          <Container sx={{ pt: 6 }}>
            <ProductListing products={products.nodes} location={location} />
            {products.pageInfo.hasNextPage && (
              <Button to={`/search?p=${slugify(productType)}#more`}>
                View more
              </Button>
            )}
          </Container>
        </Stack>
      </Container>
    </>
  )
}

export const Head = ({ pageContext, location }: ProductTypeIndexPageProps) => {
  const unslugifiedProductType = antiSlugify(pageContext.productType)
  return (
    <SeoHead
      title={`Products - ${unslugifiedProductType}`}
      pathname={location.pathname}
      description={`Blootropic ${unslugifiedProductType} products`}
    />
  )
}

export const query = graphql`
  query ProductTypeIndex ($productType: String!) {
    products: allShopifyProduct(
      filter: { productType: { eq: $productType } }
      sort: { publishedAt: ASC }
      limit: 24
    ) {
      nodes {
        ...ShopifyProductCard
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`
